import type { RangeSliderValue } from '@/app/components/shared/forms/RangeSliderField'
import { ALL_LOCATIONS } from '@/app/lib/constants'

/**
 * Estado de los filtros que cuentan para `filters_active_count` en las PLP con
 * `ListingFilters` (promociones, lo-mas-vendido, agenda…). Cada página declara
 * SOLO las dimensiones que realmente ofrece y omite las demás — de ahí que las
 * que no son universales sean opcionales. "Ordenar por" no aparece a propósito
 * — ver `countActiveFilters`.
 */
export interface ActiveFilterState {
  /** Slug de provincia; `todos` (`ALL_LOCATIONS`) significa "sin filtrar". */
  readonly province: string
  /** `genre` aplicado (query param), o cadena vacía. */
  readonly genre: string
  /**
   * Valor aplicado de la dimensión `fecha` (`filter_type=fecha`): el tramo
   * `date_range` de promociones/lo-mas-vendido o la fecha/rango (`?start_at=`,
   * `?end_at=`) de agenda. Es la MISMA dimensión de filtro, así que comparte
   * esta clave y no puede contarse dos veces. Cadena vacía = el usuario no ha
   * aplicado fecha.
   */
  readonly dateRange: string
  /** `true` si el rango de precio está acotado respecto al catálogo completo.
   * Se omite en páginas sin filtro de precio (agenda). */
  readonly priceActive?: boolean
  /** `subgenre` aplicado (query param). Solo agenda expone este filtro. */
  readonly subgenre?: string
}

/**
 * `filters_active_count` — número de filtros activos TRAS el cambio, no solo el
 * que acaba de cambiar (`dictionary/analytic-variables.md` → `filters_active_count`).
 *
 * "Ordenar por" NO cuenta como filtro: es un criterio de ordenación que no se
 * "quita". El propio listado ya lo trata así (`chip: false` en el campo de
 * `ListingFilters`, así que no genera chip de filtro aplicado) y el diccionario
 * le reserva su propio `filter_action=sort`, aparte de `apply`/`remove`.
 */
export function countActiveFilters(state: ActiveFilterState): number {
  let count = 0
  if (state.province !== ALL_LOCATIONS) count += 1
  if (state.genre) count += 1
  if (state.subgenre) count += 1
  if (state.dateRange) count += 1
  if (state.priceActive) count += 1
  return count
}

/**
 * `filter_value` de un rango de precio. No hay formato documentado para un
 * filtro de rango continuo (el diccionario solo da ejemplos de valores
 * discretos como `madrid`), así que se usa `min-max` — legible en los informes
 * y ya normalizado (solo dígitos y un guion). `0-0` es "Mostrar solo gratuitos".
 */
export function formatPriceFilterValue(range: RangeSliderValue): string {
  return `${range.min}-${range.max}`
}
